import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, UserCog } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { useAuth } from "@/context/AuthContext";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [savingName, setSavingName] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.put("/auth/profile", { full_name: fullName });
      await refresh?.();
      toast.success("Name updated");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async () => {
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSavingPass(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password changed");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSavingPass(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EEF2F8]">
      <TopBar subtitle="My settings" />
      <main className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6" data-testid="settings-page">
        <button
          data-testid="settings-back-button"
          onClick={() => navigate(-1)}
          className="mono mb-5 flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
        >
          <ArrowLeft size={12} /> Back
        </button>
        <h1 className="font-display text-3xl font-semibold tracking-tight">My settings</h1>        <p className="mt-1.5 text-sm text-slate-600">
          {user?.email} · {(user?.roles || []).join(", ") || "no role"}
        </p>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-600">
            <UserCog size={13} /> Profile
          </h2>
          <label className="mono mt-4 block text-[10px] uppercase tracking-wider text-slate-500">
            Full name
          </label>
          <Input
            data-testid="settings-name-input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1.5 border-slate-200 bg-[#EEF2F8] text-sm"
          />
          <Button
            data-testid="settings-save-name-button"
            onClick={saveName}
            disabled={savingName || fullName.trim().length < 2 || fullName === user?.full_name}
            className="mt-4 h-9 rounded-full bg-cyan-600 px-5 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500"
          >
            {savingName ? <Loader2 size={13} className="animate-spin" /> : "Save name"}
          </Button>
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-600">
            <KeyRound size={13} /> Password
          </h2>
          {[
            ["Current password", current, setCurrent, "settings-current-password"],
            ["New password (min 8 chars)", next, setNext, "settings-new-password"],
            ["Confirm new password", confirm, setConfirm, "settings-confirm-password"],
          ].map(([label, value, setter, testid]) => (
            <div key={testid} className="mt-4">
              <label className="mono block text-[10px] uppercase tracking-wider text-slate-500">
                {label}
              </label>
              <Input
                data-testid={testid}
                type="password"
                value={value}
                onChange={(e) => setter(e.target.value)}
                className="mt-1.5 border-slate-200 bg-[#EEF2F8] text-sm"
              />
            </div>
          ))}
          <Button
            data-testid="settings-save-password-button"
            onClick={savePassword}
            disabled={savingPass || !current || next.length < 8 || !confirm}
            className="mt-5 h-9 rounded-full bg-cyan-600 px-5 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500"
          >
            {savingPass ? <Loader2 size={13} className="animate-spin" /> : "Change password"}
          </Button>
        </section>

        <NotificationSettings />
      </main>
    </div>
  );
}
